import api from './api';

export interface ActivityEvent {
  id: number;
  studentId: number;
  topicId?: number; // Neo4j topic ID
  courseId?: number;
  quizId?: number;
  assessmentId?: number;
  eventType: string; // CONTENT_VIEW, TOPIC_STARTED, QUIZ_STARTED, etc.
  eventCategory: string; // CONTENT, QUIZ, ASSESSMENT, NAVIGATION, AI_INTERACTION, ACHIEVEMENT
  eventData?: Record<string, any>;
  durationSeconds?: number;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface ActivityFeedResponse {
  content: ActivityEvent[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

class ActivityService {
  /**
   * Record an activity event
   */
  async recordEvent(event: Partial<ActivityEvent>): Promise<ActivityEvent> {
    const response = await api.post<ActivityEvent>('/api/student/activity/events', {
      ...event,
      timestamp: event.timestamp || new Date().toISOString(),
    });
    return response.data;
  }

  /**
   * Get activity feed for a student
   */
  async getActivityFeed(page: number = 0, size: number = 20): Promise<ActivityFeedResponse> {
    const response = await api.get<ActivityFeedResponse>('/api/student/activity/feed', {
      params: { page, size },
    });
    return response.data;
  }

  /**
   * Get activity feed by category
   */
  async getActivityFeedByCategory(
    category: string,
    page: number = 0,
    size: number = 20
  ): Promise<ActivityFeedResponse> {
    const response = await api.get<ActivityFeedResponse>(
      `/api/student/activity/feed/category/${category}`,
      {
        params: { page, size },
      }
    );
    return response.data;
  }

  /**
   * Get activity events for a topic
   */
  async getTopicActivity(topicId: number): Promise<ActivityEvent[]> {
    const response = await api.get<ActivityEvent[]>(`/api/student/activity/topic/${topicId}`);
    return response.data;
  }

  /**
   * Helper method to record common activity events
   */
  async recordTopicView(topicId: number, courseId?: number, durationSeconds?: number) {
    return this.recordEvent({
      topicId,
      courseId,
      eventType: 'CONTENT_VIEW',
      eventCategory: 'CONTENT',
      durationSeconds,
    });
  }

  async recordTopicStarted(topicId: number, courseId?: number) {
    return this.recordEvent({
      topicId,
      courseId,
      eventType: 'TOPIC_STARTED',
      eventCategory: 'CONTENT',
    });
  }

  async recordTopicCompleted(topicId: number, courseId?: number) {
    return this.recordEvent({
      topicId,
      courseId,
      eventType: 'TOPIC_COMPLETED',
      eventCategory: 'CONTENT',
    });
  }

  async recordQuizStarted(quizId: number, topicId?: number, courseId?: number) {
    return this.recordEvent({
      quizId,
      topicId,
      courseId,
      eventType: 'QUIZ_STARTED',
      eventCategory: 'QUIZ',
    });
  }

  async recordQuizSubmitted(quizId: number, topicId?: number, courseId?: number, score?: number) {
    return this.recordEvent({
      quizId,
      topicId,
      courseId,
      eventType: 'QUIZ_SUBMITTED',
      eventCategory: 'QUIZ',
      eventData: { score },
    });
  }

  async recordAssessmentStarted(assessmentId: number, topicIds?: number[], courseId?: number) {
    return this.recordEvent({
      assessmentId,
      courseId,
      eventType: 'ASSESSMENT_STARTED',
      eventCategory: 'ASSESSMENT',
      eventData: { topicIds },
    });
  }

  async recordAssessmentSubmitted(
    assessmentId: number,
    topicIds?: number[],
    courseId?: number,
    score?: number
  ) {
    return this.recordEvent({
      assessmentId,
      courseId,
      eventType: 'ASSESSMENT_SUBMITTED',
      eventCategory: 'ASSESSMENT',
      eventData: { topicIds, score },
    });
  }
}

export default new ActivityService();
